<script>
  import { createEventDispatcher, onMount, getContext } from 'svelte';
  import { fade } from 'svelte/transition';
  import tippy from 'tippy.js';
  import { goto } from '$app/navigation';

  import Alert from '@components/Alert.svelte';
  import Text from '@components/Text.svelte';
  import VisibilityGuard from '@components/VisibilityGuard.svelte';
  import ScoreBadge from '@components/ScoreBadge.svelte';
  import EmojiPicker from '@app-components/EmojiPicker.svelte';
  import ListAuthors from '@app-components/ListAuthors.svelte';
  import DuplicateListModal from '@app-components/modal/DuplicateListModal.svelte';
  import ListSettingsModal from '@app-components/modal/ListSettingsModal.svelte';
  import MoveToCollectionModal from '@app-components/modal/MoveToCollectionModal.svelte';
  import ShareModal from '@app-components/modal/ShareModal.svelte';
  import Notification from '@utils/notification';
  import { getCurrentUserStore } from '@stores/current_user';
  import GroupAjax from '@ajax/group';
  import WelcomeNoteActiveIndicator from '../WelcomeNoteActiveIndicator.svelte';
  import GroupOptimizedIndicator from '../GroupOptimizedIndicator.svelte';
  import TileCoverPhotos from './TileCoverPhotos.svelte';
  import ChangeCoverPhotosModal from '@app-components/modal/ChangeCoverPhotosModal.svelte';
  import { browser } from '$app/environment';

  import { getProfileStore } from '@stores/profile';
  import { getGlobalStore } from '@stores/global';

  import 'tippy.js/themes/light.css';
  import ListCopier from '@app-components/group/ListCopier.svelte';
  import GroupActionsBar from '@app-components/group/GroupActionsBar.svelte';
  import HideReportedContentOverlay from '@app-components/report/HideReportedContentOverlay.svelte';

  const dispatch = createEventDispatcher();

  export let id;
  export let user;
  export let groupCollectionId;
  export let mobile = false;
  export let score = null;
  export let showSpotlightTile = false;
  let reportedContentKey = `group_${id}`;
  let reportedContent = getContext('reportedContent');
  $: reportedContentId = $reportedContent[reportedContentKey] || null;
  let currentUser = getCurrentUserStore('default');
  let groupsStore = getProfileStore('groupsStore');
  let groupCollectionStore = getProfileStore('groupCollectionStore');
  let reportContentStore = getGlobalStore('reportContentStore');
  let group = groupsStore.find(id);
  export let groupCollections;

  let showEmoji = false;
  let showOptionsMenu = false;
  let editingText = false;

  let showDeleteModal = false;
  let showDuplicateListModal = false;
  let moveToCollectionModal = false;
  let listSettingsModal = false;
  let showShareModal = false;
  let showCoverPhotosModal = false;

  let settingsSelectedOptionIndex = 1;
  export let showDetailsButton = true;
  export let showWelcomeNoteFlag = true;
  export let bookmarkId = null;

  $: editable = group && group.permissions && group.permissions.edit;
  $: isHidden = group && group.listing_flag == 'unlisted';
  $: isPrivate = group && group.privacy_flag == 'private';
  $: authors = group && group.users.filter((u) => u.role != 'viewer');
  $: owner = group && group.users.filter((u) => u.role == 'admin')[0];

  let openEmojiSelector = function () {
    if (editable && !mobile) {
      showEmoji = true;
    }

    dispatch('emoji-select-clicked', {
      groupId: group.id,
    });
  };

  let closeEmojiSelector = function () {
    showEmoji = false;
    if (bookmarkId == null) {
      dispatch('emoji-select-closed', {
        groupId: group.id,
      });
    }
  };

  let showOptions = function () {
    showOptionsMenu = true;
    if (bookmarkId == null) {
      dispatch('options-select-clicked', {
        groupId: group.id,
      });
    }
  };

  let closeOptions = function () {
    showOptionsMenu = false;
    if (bookmarkId == null) {
      dispatch('options-select-closed', {
        groupId: group.id,
      });
    }
  };

  let displayMoveToBookmarkCollectionModal = function () {
    closeOptions();
    dispatch('move-to-bookmark-collection', { bookmarkId: bookmarkId });
  };

  let displayRemoveBookmarkModal = function () {
    closeOptions();
    dispatch('remove-bookmark', { bookmarkId: bookmarkId });
  };

  let displayShareModal = function () {
    closeOptions();
    showShareModal = true;
  };

  let displayCoverPhotosModal = function () {
    closeOptions();
    showCoverPhotosModal = true;
  };

  let setupTippy = function (node) {
    let tippyInstance = tippy(node);
    return {
      destroy() {
        tippyInstance.destroy();
      },
    };
  };

  let setupTippyForBrokenLink = function (node, targetUrl) {
    var tippyObj;
    var setup = function (targetUrl) {
      if (targetUrl == '' && tippyObj) {
        tippyObj.destroy();
        tippyObj = undefined;
      } else if (targetUrl && targetUrl != '') {
        var content = `<a href="${targetUrl}" style='color: white; word-wrap: break-word;'>Broken links detected!</a>`;

        if (tippyObj) {
          tippyObj.setContent(content);
        } else {
          tippyObj = tippy(node, {
            content,
            allowHTML: true,
            interactive: true,
            placement: 'bottom',
          });
        }
      }
    };

    setup(targetUrl);

    return {
      update(x) {
        setup(x);
      },
    };
  };

  let tileClicked = function (e) {
    if (!editingText) {
      dispatch('tile-clicked', { event: e });
    }
  };

  let selectEmoji = function (emoji) {
    group.emoji.outside_src = emoji.outside_src;
    dispatch('emoji-selected', { emoji: emoji });
  };

  let goToPrivacySettings = function (e) {
    if (editable) {
      e.preventDefault();
      e.stopPropagation();

      if (!mobile) {
        settingsSelectedOptionIndex = 1;
        showListSettingsModal();
      }

      dispatch('list-privacy-clicked');
    }
  };

  let showListSettingsModal = function () {
    closeOptions();
    listSettingsModal = true;
  };

  let showMoveToCollectionModal = function () {
    closeOptions();
    moveToCollectionModal = true;
  };

  let hideListSettingsModal = function () {
    listSettingsModal = false;
  };

  let hideMoveToCollectionModal = function () {
    moveToCollectionModal = false;
  };

  let extractText = function (html) {
    // Remove all HTML tags using a regular expression
    return html.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, ' ');
  };

  onMount(() => {
    let sub = group.subscribe((x) => (group = x));
    let subGC = groupCollectionStore.subscribe((x) => (groupCollections = x));

    return () => {
      sub();
      subGC();
    };
  });
</script>

<style style="text/scss" lang="scss">
  .tile-container {
    position: relative;
    height: inherit;
  }

  .tile {
    color: black;
    position: relative;
    cursor: pointer;
    border-radius: 15px;
    flex-direction: column;
    display: flex;
    background: #fff;
    width: 100%;
    box-shadow: 0 12px 64px -20px rgba(0, 0, 0, 0.2);
    padding-left: 10px;
    padding-right: 10px;
    padding: 20px;
    border: 2px solid transparent;
    transition: 0.25s;
    height: 100%;
    padding-top: 10px !important;

    &:hover {
      box-shadow: 0 1.375rem 6.25rem rgba(0, 0, 0, 0.18);
    }

    .top-bar {
      display: flex;
      justify-content: space-between;
      position: relative;

      .back-btn {
        position: absolute;
        right: -7px;
        top: -5px;
        padding: 10px;
        background: transparent;
        border: none;
        cursor: pointer;
        display: inline-block;

        &:hover {
          path {
            stroke: #a893f9;
          }
        }
      }

      .editable.emoji {
        cursor: pointer;

        &:hover {
          background: #ede9ff;
        }
      }

      .emoji {
        width: 46px;
        height: 46px;
        border-radius: 7px;
        display: flex;
        justify-content: center;
        align-items: center;
        transition: 0.25s;
        margin-top: 10px;

        .icon {
          width: 32px;
          height: 32px;
          position: relative;

          img {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
            flex-shrink: 0;
            display: inline-block;
          }
        }
      }

      .tile-options {
        display: flex;
        transition: 0.25s;

        .tile-menu {
          padding-top: 10px;
          padding-right: 7px;
          display: inline-block;
          background: transparent;
          border: none;
          cursor: pointer;
          padding-left: 7px;

          &:hover {
            path {
              fill: #a893f9 !important;
            }
          }

          path {
            fill: rgb(213, 207, 234);
          }
        }

        .tile-btn {
          padding-top: 10px;
          margin-right: 10px;
          background: transparent;
          border: none;
          cursor: pointer;
          display: inline-block;

          svg {
            height: 20px;
            width: 20px;
          }

          path {
            fill: #d5cfea;
            transition: 0.25s;
          }
        }
      }
    }

    .main-content {
      margin-left: 10px;
      margin-right: 10px;

      .group-metadata-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        margin-top: -2px;
      }

      .group-name-container {
        height: 60px;
        display: flex;
        flex-direction: column-reverse;

        .group-name {
          word-break: break-word;
          line-height: 1.4;
          font-size: 18px;
          font-weight: 500;
          user-select: text;
        }
      }

      .tile-group-description {
        font-size: 14px;
        line-height: 20px;
        color: #7f7f7f;
        margin-top: 14px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        margin-left: 4px;
        margin-bottom: 6px;
      }

      .items-count {
        font-size: 16px;
        line-height: 20px;
        color: #7f7f7f;
        margin-top: 10px;
        &.bookmarks {
          margin-top: 0px;
        }
      }

      .authors {
        margin-top: 8px;
      }
      .groups-authors {
        margin-top: 24px;
      }
    }

    .tile-group-bottom-bar {
      margin-top: auto;
      width: 100%;
      margin-left: 4px;
      margin-right: 10px;
      padding-top: 10px;
      margin-bottom: -10px;
    }

    &.cover-photos-tile {
      padding: 20px;

      .top-bar {
        justify-content: space-between;
        min-height: 24px;

        .emoji {
          display: none;
        }
      }

      .group-name-container {
        margin-top: 12px;
        margin-left: 4px;
        width: 100%;
      }

      .group-metadata-container {
        margin-left: 4px;
      }
    }

    &.tile-options-menu {
      cursor: unset;
      z-index: 100;
      position: absolute;
      width: 320px;
      height: min-content;
    }
  }

  .menu-options {
    display: flex;
    flex-direction: column;
    padding: 10px;
    height: min-content;
    margin-bottom: -5px;

    .menu-options-view-list {
      a {
        font-weight: 500;

        &:hover {
          color: #a893f9;
        }
      }
    }

    * :global(.link),
    .link {
      cursor: pointer;
      font-weight: 500;
      &:hover {
        color: #a893f9;
      }
    }

    .menu-options-privacy {
      margin-top: 20px;

      a {
        font-weight: 500;

        &:hover {
          color: #a893f9;
        }
      }
    }

    .menu-options-duplicate {
      margin-top: 15px;

      a {
        font-weight: 500;

        &:hover {
          color: #a893f9;
        }
      }
    }

    .menu-options-members {
      margin-top: 15px;

      a {
        font-weight: 500;

        &:hover {
          color: #a893f9;
        }
      }
    }

    .menu-options-delete {
      margin-top: 15px;

      a {
        font-weight: 500;
        color: #ff5567;

        &:hover {
          color: #99333d;
        }
      }
    }
  }

  .emoji-container {
    position: relative;
    left: 30px;
    top: 75px;
    width: 360px;
    z-index: 99;
  }

  .yes-delete-btn {
    height: 48px;
    color: #ff5567;
    font-weight: 500;
    font-size: 14px;
    border: none;
    border-radius: 15px;
    background: #fde7e7;
    cursor: pointer;
    display: block;
    width: 100%;

    &:hover {
      background: #ff5567;
      color: white;
    }
  }

  .cancel-btn {
    width: 100%;
    color: #000;
    font-size: 14px;
    margin-top: 8px;
    border: none;
    background: transparent;
    cursor: pointer;
    font-weight: 500;
    margin-bottom: -10px;
  }

  .mobile {
    height: 235px;
    width: 300px;
    box-shadow: 0 6px 32px -10px rgba(0, 0, 0, 0.2);

    &.tile-options-menu {
      width: 300px;
    }

    &:hover {
      box-shadow: 0 6px 32px -10px rgba(0, 0, 0, 0.3);
    }

    .group-name {
      font-size: 15px !important;
    }

    .items-count {
      font-size: 14px !important;
    }

    .tile-btn {
      margin-right: 2px !important;
    }

    &.cover-photos-tile {
      height: 100%;
    }
    &.bookmarks {
      height: 140px !important;
    }

    .menu-options {
      height: 100%;
    }
  }
  .left-side {
    margin-left: 10px;

    padding-top: 10px;
  }
</style>

{#if showDeleteModal}
  <Alert
    mobile={mobile}
    on:yes-click={() => {
      dispatch('delete-clicked');
    }}
    question="Are you sure you want to delete this?"
    yesButtonText="Delete"
    objectText={group.name}
    bind:show={showDeleteModal} />
{/if}

{#if showDuplicateListModal}
  <DuplicateListModal
    mobile={mobile}
    on:success={(e) => {
      dispatch('new-duplicate-created', { group: e.detail.group });
    }}
    bind:showModal={showDuplicateListModal}
    group={group} />
{/if}

{#if listSettingsModal}
  <ListSettingsModal
    mobile={mobile}
    group={group}
    bind:selectedOptionIndex={settingsSelectedOptionIndex}
    showModal={listSettingsModal}
    on:close={() => {
      hideListSettingsModal();
    }}
    on:duplicate={(e) => {
      dispatch('new-duplicate-created', { group: e.detail.group });
    }} />
{/if}

{#if moveToCollectionModal}
  <MoveToCollectionModal
    mobile={mobile}
    groupId={group.id}
    groupCollectionId={groupCollectionId}
    showModal={moveToCollectionModal}
    on:close={() => {
      hideMoveToCollectionModal();
    }} />
{/if}

{#if showShareModal}
  <ShareModal mobile={mobile} bind:showShareModal={showShareModal} slug={group.slug} group={group} />
{/if}

{#if showCoverPhotosModal}
  <ChangeCoverPhotosModal bind:showModal={showCoverPhotosModal} mobile={mobile} group={group} user={user} />
{/if}

<div style={mobile ? 'position: relative' : ''} class="tile-container">
  {#if showOptionsMenu}
    <div class="backdrop" on:click|preventDefault|stopPropagation={closeOptions} in:fade={{ duration: 100 }} />

    <div
      class="tile tile-options-menu"
      class:cover-photos-tile={user.show_cover_photos_for_groups}
      class:bookmarks={bookmarkId != null}
      class:mobile={mobile}
      class:spotlight-tile={showSpotlightTile}>
      <div class="top-bar" in:fade={{ duration: 100 }}>
        <button class="back-btn" on:click|preventDefault|stopPropagation={closeOptions}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id="mask0" mask-type="alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="12" height="12">
              <rect width="12" height="12" fill="white" />
            </mask>
            <g mask="url(#mask0)">
              <path
                d="M7.14978 6.08842C7.13814 6.07681 7.1289 6.06302 7.1226
              6.04783C7.1163 6.03265 7.11305 6.01637 7.11305 5.99993C7.11305
              5.98349 7.1163 5.96721 7.1226 5.95202C7.1289 5.93683 7.13814
              5.92304 7.14978 5.91143L11.7811 1.28062C11.9217 1.13979 12.0006
              0.948872 12.0005 0.749857C12.0004 0.550842 11.9212 0.360034
              11.7803 0.219408C11.6395 0.0787831 11.4486 -0.000140457 11.2496
              1.87653e-07C11.0506 0.000140832 10.8598 0.0793341 10.7191
              0.220158L6.08832 4.84897C6.07671 4.86061 6.06292 4.86985 6.04773
              4.87615C6.03254 4.88245 6.01626 4.8857 5.99982 4.8857C5.98338
              4.8857 5.9671 4.88245 5.95192 4.87615C5.93673 4.86985 5.92294
              4.86061 5.91133 4.84897L1.28051 0.220158C1.21088 0.150495 1.12821
              0.0952284 1.03722 0.0575143C0.946233 0.0198001 0.848706
              0.000376929 0.75021 0.000353716C0.551289 0.000306834 0.360496
              0.079283 0.219805 0.219908C0.079113 0.360534 4.69031e-05 0.551289
              2.08609e-08 0.75021C-4.68614e-05 0.949132 0.0789293 1.13992
              0.219555 1.28062L4.84987 5.91143C4.86151 5.92304 4.87074 5.93683
              4.87705 5.95202C4.88335 5.96721 4.88659 5.98349 4.88659
              5.99993C4.88659 6.01637 4.88335 6.03265 4.87705 6.04783C4.87074
              6.06302 4.86151 6.07681 4.84987 6.08842L0.219555 10.7197C0.149924
              10.7894 0.0946965 10.8721 0.0570253 10.9631C0.019354 11.0541
              -2.31929e-05 11.1516 2.08609e-08 11.2501C4.69031e-05 11.4491
              0.079113 11.6398 0.219805 11.7804C0.289468 11.8501 0.372164
              11.9053 0.463171 11.943C0.554179 11.9806 0.651715 12 0.75021
              12C0.949132 12 1.13989 11.9209 1.28051 11.7802L5.91133
              7.14888C5.92294 7.13724 5.93673 7.12801 5.95192 7.1217C5.9671
              7.1154 5.98338 7.11216 5.99982 7.11216C6.01626 7.11216 6.03254
              7.1154 6.04773 7.1217C6.06292 7.12801 6.07671 7.13724 6.08832
              7.14888L10.7191 11.7802C10.8598 11.9209 11.0505 12 11.2494
              12C11.4484 12 11.6391 11.9211 11.7798 11.7804C11.9205 11.6398
              11.9996 11.4491 11.9996 11.2501C11.9997 11.0512 11.9207 10.8604
              11.7801 10.7197L7.14978 6.08842Z"
                fill="black" />
            </g>
          </svg>
        </button>
      </div>

      <div class="menu-options">
        {#if bookmarkId == null}
          <div class="menu-options-view-list">
            <a
              href="#"
              on:click|preventDefault|stopPropagation={(e) => {
                tileClicked(e);
              }}>
              View list
            </a>
          </div>
          {#if editable}
            <div class="menu-options-members">
              <a
                href="#"
                on:click|preventDefault|stopPropagation={() => {
                  showListSettingsModal();
                  dispatch('list-settings-clicked');
                }}>
                List settings
              </a>
            </div>
            {#if user.show_cover_photos_for_groups}
              <div class="menu-options-members">
                <a
                  href="#"
                  on:click|preventDefault|stopPropagation={() => {
                    displayCoverPhotosModal();
                  }}>
                  Edit cover photos
                </a>
              </div>
            {/if}
            <div class="menu-options-duplicate">
              <a
                href="#"
                on:click|preventDefault|stopPropagation={() => {
                  showDuplicateListModal = true;
                  closeOptions();
                }}>
                Duplicate list
              </a>
            </div>
          {/if}
          <div class="menu-options-members">
            {#if editable}
              <a
                href="#"
                on:click|preventDefault|stopPropagation={() => {
                  displayShareModal();
                }}>
                Share list
              </a>
            {:else}
              <ListCopier
                group={group}
                mobile={mobile}
                relatedUserId={owner.id}
                on:link-copied={() => {
                  showOptionsMenu = false;
                }}>
                <span class="link"> Share list </span>
              </ListCopier>
            {/if}
          </div>
          {#if user.id === currentUser.id && groupCollections.length > 1 && !mobile}
            <div class="menu-options-members">
              <a
                href="#"
                on:click|preventDefault|stopPropagation={() => {
                  showMoveToCollectionModal();
                  dispatch('move-to-collection');
                }}>
                Move to collection
              </a>
            </div>
          {/if}
          {#if user.id !== currentUser.id}
            <div class="menu-options-members">
              <a
                href="#"
                on:click|preventDefault|stopPropagation={() => {
                  showOptionsMenu = false;
                  reportContentStore.set({ show: true, params: { group_base_id: group.id }, owner: user });
                }}>
                Report list
              </a>
            </div>
          {/if}
          {#if user.id === currentUser.id && mobile}
            <div class="menu-options-members">
              <a
                href="#"
                on:click|preventDefault|stopPropagation={() => {
                  goto('/profiles/list_organizer');
                }}>
                Organize lists
              </a>
            </div>
          {/if}
          {#if editable}
            <div class="menu-options-delete">
              <a
                href="#"
                on:click|preventDefault|stopPropagation={() => {
                  showOptionsMenu = false;
                  showDeleteModal = true;
                  dispatch('delete-menu-clicked');
                }}>
                Delete
              </a>
            </div>
          {/if}
        {:else}
          <div class="menu-options-members">
            <a
              href="#"
              on:click|preventDefault|stopPropagation={() => {
                displayMoveToBookmarkCollectionModal();
              }}>
              Add to folder
            </a>
          </div>
          <div class="menu-options-delete">
            <a
              href="#"
              on:click|preventDefault|stopPropagation={() => {
                displayRemoveBookmarkModal();
              }}>
              Remove
            </a>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  {#if editable && showEmoji}
    <div
      class="backdrop-darker"
      on:click|preventDefault|stopPropagation={closeEmojiSelector}
      in:fade={{ duration: 100 }} />

    <div in:fade={{ duration: 100 }} class="emoji-container">
      <EmojiPicker
        mobile={mobile}
        on:emoji-selected={(e) => {
          closeEmojiSelector();
          selectEmoji(e.detail.emoji);
        }} />
    </div>
  {/if}

  <a href={group.path} style="display: contents;" data-sveltekit-preload-data>
    <div
      class="tile draggable-tile"
      class:cover-photos-tile={user.show_cover_photos_for_groups}
      class:mobile={mobile}
      on:click|preventDefault|stopPropagation={(e) => {
        tileClicked(e);
      }}>
      <div class="top-bar" in:fade={{ duration: 100 }}>
        <div class="left-side">
          {#if score}
            <ScoreBadge score={score} />
          {/if}
        </div>
        <div class="right-side">
          <div
            class="emoji undraggable"
            class:editable={editable && !mobile}
            on:click|preventDefault|stopPropagation={openEmojiSelector}>
            <div class="icon">
              <VisibilityGuard let:hasBeenVisible>
                {#if hasBeenVisible}
                  <img src={group.emoji.outside_src} alt="List Emoji" />
                {/if}
              </VisibilityGuard>
            </div>
          </div>

          <div class="tile-options" in:fade={{ duration: 100 }}>
            {#if showWelcomeNoteFlag && browser}
              <GroupOptimizedIndicator
                location={user.show_cover_photos_for_groups ? 'profileCoverPhotos' : 'profileNew'}
                groupId={id}
                mobile={mobile} />
            {/if}
            {#if showWelcomeNoteFlag && browser}
              <WelcomeNoteActiveIndicator
                location={user.show_cover_photos_for_groups ? 'profileCoverPhotos' : 'profileNew'}
                groupId={id}
                mobile={mobile} />
            {/if}
            {#if currentUser && currentUser.id && isPrivate}
              <button
                class="tile-btn"
                use:setupTippy
                data-tippy-content="Link sharing is off"
                on:click|preventDefault|stopPropagation={goToPrivacySettings}>
                <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M13.4874 15.4934C13.8156 15.169 14 14.729 14
                14.2703V9.08108C14 8.62233 13.8156 8.18237 13.4874
                7.85798C13.1592 7.53359 12.7141 7.35135 12.25
                7.35135H11.8125V4.75676C11.8125 3.49519 11.3055 2.28529 10.403
                1.39322C9.50043 0.501157 8.27635 0 7 0C5.72365 0 4.49957
                0.501157 3.59705 1.39322C2.69453 2.28529 2.1875 3.49519 2.1875
                4.75676V7.35135H1.75C1.28587 7.35135 0.840752 7.53359 0.512563
                7.85798C0.184375 8.18237 0 8.62233 0 9.08108V14.2703C0 14.729
                0.184375 15.169 0.512563 15.4934C0.840752 15.8178 1.28587 16
                1.75 16H12.25C12.7141 16 13.1592 15.8178 13.4874
                15.4934ZM8.5468 3.22788C8.95703 3.63336 9.1875 4.18332 9.1875
                4.75676V7.35135H4.8125V4.75676C4.8125 4.18332 5.04297 3.63336
                5.4532 3.22788C5.86344 2.82239 6.41984 2.59459 7
                2.59459C7.58016 2.59459 8.13656 2.82239 8.5468 3.22788Z"
                    fill="#896BD9" />
                </svg>
              </button>
            {/if}

            {#if currentUser && currentUser.id && isHidden}
              <button
                class="tile-btn"
                use:setupTippy
                data-tippy-content="This list is hidden"
                on:click|preventDefault|stopPropagation={goToPrivacySettings}>
                <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M1.49304 1.48649C1.15252 1.14598 1.15252 0.595907 1.49304
                0.255389C1.83356 -0.0851296 2.39236 -0.0851296 2.73288
                0.255389L16.9735 14.5135C17.3141 14.854 17.3141 15.4041
                16.9735 15.7446C16.633 16.0851 16.0829 16.0851 15.7424
                15.7446L13.3675 13.3697C12.1975 13.8237 10.9315 14.0857
                9.60437 14.0857C5.23874 14.0857 1.5105 11.3703 0
                7.53724C0.672306 5.81719 1.79864 4.33288 3.2131
                3.20655L1.49304 1.48649ZM13.9701 7.53837C13.9701 5.12855
                12.0143 3.17275 9.60446 3.17275C9.15917 3.17275 8.73133
                3.26006 8.32097 3.3823L6.42629 1.48762C7.43038 1.16456 8.49559
                0.989939 9.60446 0.989939C13.9701 0.989939 17.6983 3.70535
                19.2088 7.52964C18.6064 9.06634 17.6459 10.4109 16.4323
                11.4849L13.7605 8.81313C13.8828 8.41149 13.9701 7.98366
                13.9701 7.53837ZM9.60437 11.9026C7.19454 11.9026 5.23874
                9.94679 5.23874 7.53697C5.23874 6.86467 5.39591 6.22728
                5.66658 5.66849L7.03738 7.03929C7.01119 7.19645 6.98499
                7.36235 6.98499 7.53697C6.98499 8.98636 8.15498 10.1563
                9.60437 10.1563C9.77899 10.1563 9.93615 10.1302 10.102
                10.0952L11.4729 11.466C10.9053 11.7454 10.2767 11.9026 9.60437
                11.9026ZM12.1974 7.24962C12.0664 6.02725 11.106 5.07555
                9.89235 4.94458L12.1974 7.24962Z"
                    fill="#896BD9" />
                </svg>
              </button>
            {/if}

            {#if showDetailsButton || bookmarkId}
              <button class="tile-menu undraggable" on:click|preventDefault|stopPropagation={showOptions}>
                {#if mobile}
                  <img src="/images/profile/horizontal-dots.svg" />
                {:else}
                  <svg width="20" height="20" fill="none" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <mask id="a" x="0" y="0" width="20" height="20" mask-type="alpha" maskUnits="userSpaceOnUse">
                      <rect width="20" height="20" fill="#fff" />
                    </mask>
                    <g mask="url(#a)">
                      <path
                        d="m2.5 12.5c-1.3807 0-2.5-1.1193-2.5-2.5-6.0353e-8 -1.3807 1.1193-2.5 2.5-2.5s2.5 1.1193 2.5 2.5c0 1.3807-1.1193 2.5-2.5 2.5zm15 0c-1.3807 0-2.5-1.1193-2.5-2.5 0-1.3807 1.1193-2.5 2.5-2.5s2.5 1.1193 2.5 2.5c0 1.3807-1.1193 2.5-2.5 2.5zm-10-2.5c0 1.3807 1.1193 2.5 2.5 2.5 1.3807 0 2.5-1.1193 2.5-2.5 0-1.3807-1.1193-2.5-2.5-2.5-1.3807 0-2.5 1.1193-2.5 2.5z"
                        clip-rule="evenodd"
                        fill="#EAE4FF"
                        fill-rule="evenodd" />
                    </g>
                  </svg>
                {/if}
              </button>
            {/if}
          </div>
        </div>
      </div>

      <div class="main-content">
        {#if user.show_cover_photos_for_groups}
          <div class="image-container">
            {#if group.minified_cover_image}
              <img
                src={group.minified_cover_image}
                alt="{group.name} cover photo collage"
                style={mobile
                  ? 'width: 236px; height: 190px; border-radius: 10px;'
                  : 'height: 203px; width: 252px; border-radius: 10px;'} />
              <div class="image-subtle-overlay radius-10"></div>
            {:else}
              <TileCoverPhotos group={group} mobile={mobile} />
              {#if group.cover_photos.length > 0}
                <div class="image-subtle-overlay radius-12"></div>
              {/if}
            {/if}
          </div>
        {/if}

        <div class="group-name-container">
          <span class="group-name">
            <Text
              lines={2}
              maxHeight={58}
              text={editable
                ? group.name
                : `<h2 style='margin-top: 0px; margin-bottom: 0px; line-height: 1.4; font-size: ${
                    mobile ? 15 : 18
                  }px;' >${group.name}</h2>`}
              expand={false}
              editable={editable && !mobile && showDetailsButton}
              on:focus={() => {
                editingText = true;
              }}
              on:blur={async (e) => {
                setTimeout(() => {
                  editingText = false;
                }, 500);

                let data = {
                  name: e.detail.text,
                };

                try {
                  let resp = await GroupAjax.update(group, data);
                  group.set({ ...group, ...resp });
                } catch (err) {
                  if (err && err.full_errors.length > 0 && err.full_errors[0]) {
                    Notification.send({
                      text: err.full_errors[0],
                    });
                    group = group;
                  }
                }
              }} />
          </span>
        </div>

        <div class="group-metadata-container">
          {#if group && group.permissions && group.permissions.edit && group.broken_weblinks_count && group.broken_weblinks_count > 0}
            <img
              use:setupTippyForBrokenLink={group.path}
              src="/images/alert.svg"
              style="width: 16px; margin-top: 10px;" />
          {/if}
          {#if group.items_count}
            <div class="items-count {bookmarkId != null ? 'bookmarks' : ''}">
              {group.items_count}
              {group.items_count == 1 ? 'item' : 'items'}
            </div>
          {/if}
          {#if bookmarkId == null}
            <div class="authors">
              <ListAuthors
                nameMaxLinesShown={2}
                condensedView={true}
                group={group}
                showContributorModal={false}
                showContributorsTippyLabel={true}
                mobile={mobile}
                disableHyperlink={true}
                showNames={!mobile}
                contributorText={mobile ? '+[count]' : '+[remainingCount]'}
                authors={authors}
                skipBy={true} />
            </div>
          {/if}
        </div>
        {#if bookmarkId != null}
          <div class="groups-authors">
            <ListAuthors
              nameMaxLinesShown={1}
              group={group}
              showContributorModal={false}
              showContributorsTippyLabel={true}
              disableHyperlink={true}
              authors={authors}
              mobile={mobile}
              skipBy={true} />
          </div>
        {/if}

        {#if group.description && extractText(group.description) != ''}
          <div class="tile-group-description">{extractText(group.description)}</div>
        {/if}
      </div>

      <div class="tile-group-bottom-bar">
        <GroupActionsBar groupStore={group} group={group} displayStyle="tile" />
      </div>
    </div>
  </a>
  {#if reportedContentId}
    <HideReportedContentOverlay
      text="Show List"
      reportedContentId={reportedContentId}
      reportedContentKey={reportedContentKey} />
  {/if}
</div>
